import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

// Slim identity strip + "Save & Return" used by focused (embed) pop-outs.
export function EmbedStrip({ title, hint, data, onBeforeClose, testId }) {
  const [busy, setBusy] = useState(false);
  const done = async () => {
    setBusy(true);
    try { if (onBeforeClose) await onBeforeClose(); window.close(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const chip = (t) => <span className="px-2 py-0.5 bg-white border border-amber-200 rounded">{t}</span>;
  return (
    <div data-testid={testId} className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 flex items-start justify-between gap-4 sticky top-0 z-10 shadow-sm">
      <div>
        <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold">{title}</div>
        <div className="text-sm text-slate-700 mt-1">{hint}</div>
        <div className="flex items-center gap-2 text-xs text-slate-700 mt-2 flex-wrap">
          {data?.appraisal_no ? chip(<b>A{data.appraisal_no}</b>) : null}
          {data?.client?.client_no ? chip(`C${data.client.client_no}`) : null}
          {data?.property?.property_no ? chip(`P${data.property.property_no}`) : null}
          {(data?.quotation_type || data?.quote_type_ref) ? chip(data.quotation_type || data.quote_type_ref) : null}
        </div>
      </div>
      <button data-testid="embed-save-return-btn" onClick={done} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md bg-[#0F4F1B] text-white hover:bg-[#0a3c14] disabled:opacity-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save &amp; Return
      </button>
    </div>
  );
}

const WORD_FIELDS = [['appraisal_action_general', 'Recommended work (general action)', 4], ['roof_condition', 'Roof condition', 4], ['appraisal_comments', 'Appraisal comments', 5]];

// Focused wording editor — only the text fields that feed the documents.
export function WordingQuickForm({ api, appraisalId, data, registerSave }) {
  const [form, setForm] = useState(() => Object.fromEntries(WORD_FIELDS.map(([k]) => [k, data?.[k] || ''])));
  const [orig, setOrig] = useState(form);
  const dirty = WORD_FIELDS.some(([k]) => (form[k] || '') !== (orig[k] || ''));
  const save = async () => {
    if (!dirty) return;
    await api.put(`/appraisals/${appraisalId}`, form);
    setOrig(form);
    toast.success('Wording saved');
  };
  useEffect(() => { registerSave(save); });  // strip's Save & Return saves first (pass save itself — ref.current() must call it)
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="embed-wording-form">
      {WORD_FIELDS.map(([k, label, rows]) => (
        <label key={k} className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
          <textarea data-testid={`embed-wording-${k}`} rows={rows} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
            className="border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-400" />
        </label>
      ))}
      <div className="flex items-center gap-3">
        <button data-testid="embed-wording-save-btn" disabled={!dirty} onClick={() => save().catch(e => toast.error(e?.response?.data?.detail || 'Save failed'))}
          className="px-4 py-1.5 text-sm rounded border-2 border-amber-500 text-amber-800 font-semibold disabled:opacity-40">Save wording</button>
      </div>
    </div>
  );
}
